// trace: Top-level route tree.
//
// Three host modes:
//   • Master tenant (chitos.<apex> or bare localhost) — landing page at /,
//     OTP login at /login, regular Shell when authenticated. Operators
//     whose JWT carries the `platform:write` scope additionally see
//     /admin/tenants /admin/flags /admin/analytics nav.
//   • Tenant subdomain (<sub>.<apex>) — bare / redirects to /login; rest
//     of the Shell looks identical.
//   • Subscriber portal (any host, kind=subscriber session) — SubShell.

import type { ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { useAuth } from './auth.js';
import { is_marketing_host } from './lib/marketing.js';
import { Shell } from './components/Shell.js';
import { Splash } from './components/Splash.js';
import { LoginPage } from './routes/login.js';
import { LandingPage } from './routes/landing.js';
import { DocsPage } from './routes/docs.js';
import { LegalPage } from './routes/legal.js';
import { SignupPage } from './routes/signup.js';
import { SetPasswordPage } from './routes/set-password.js';
import { DashboardPage } from './routes/dashboard.js';
import { ChitsPage } from './routes/chits.js';
import { ChitsNewPage } from './routes/chits-new.js';
import { ChitDetailPage } from './routes/chit-detail.js';
import { DuesPage } from './routes/dues.js';
import { SubscribersPage } from './routes/subscribers.js';
import { SubscribersNewPage } from './routes/subscribers-new.js';
import { SubscriberDetailPage } from './routes/subscriber-detail.js';
import { SubscriberChitDetailPage } from './routes/subscriber-chit-detail.js';
import { WhatsAppPage } from './routes/whatsapp.js';
import { CallsPage } from './routes/calls.js';
import { ReportsPage } from './routes/reports.js';
import { ReportsAgingPage } from './routes/reports-aging.js';
import { ReportsChitPnlPage } from './routes/reports-chit-pnl.js';
import { ReportsCollectionDayPage } from './routes/reports-collection-day.js';
import { ReportsCommsPage } from './routes/reports-comms.js';
import { ReportsDayBookPage } from './routes/reports-day-book.js';
import { ReportsForemanCommissionPage } from './routes/reports-foreman-commission.js';
import { ReportsMemberStatementPage } from './routes/reports-member-statement.js';
import { ReportsPayoutForecastPage } from './routes/reports-payout-forecast.js';
import { TransactionsPage } from './routes/transactions.js';
import { SettingsChannelsPage } from './routes/settings-channels.js';
import { SettingsPage } from './routes/settings.js';
import { VerifyPage } from './routes/verify.js';
import { TaxPage } from './routes/tax.js';
import { HelpPage } from './routes/help.js';
import { FeatureGate } from './components/Entitlement.js';
import { ProfilePage } from './routes/profile.js';
import { SubShell } from './sub/SubShell.js';
import { SubLogin } from './sub/SubLogin.js';
import { SubHome } from './sub/SubHome.js';
import { SubChitDetail } from './sub/SubChitDetail.js';
import { SubPay } from './sub/SubPay.js';
import { SubHistory } from './sub/SubHistory.js';
import { SubCredit } from './sub/SubCredit.js';
import { AdminTenants } from './admin/AdminTenants.js';
import { AdminTenantDetail } from './admin/AdminTenantDetail.js';
import { AdminPlans } from './admin/AdminPlans.js';
import { AdminTransactions } from './admin/AdminTransactions.js';
import { AdminFlags } from './admin/AdminFlags.js';
import { AdminAnalytics } from './admin/AdminAnalytics.js';

/**
 * Master-tenant detection. The master host is the only place that serves the
 * marketing landing page; every other tenant subdomain redirects `/` to
 * `/login`. We check the first DNS label here — the matching backend
 * resolution is in extract_tenant_subdomain (context.ts).
 *
 * Matches:
 *   • host === "chitos.<apex>"  → master tenant (production)
 *   • host === "localhost"      → master tenant (dev — no subdomain at all)
 */
function is_master_host(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname.toLowerCase();
  if (host === 'localhost') return true;
  const first = host.split('.')[0];
  return first === 'chitos';
}

export function App(): ReactElement {
  const { loading, session } = useAuth();
  if (loading) return <Splash />;

  // Public marketing apex (chitdesk.com / www.chitdesk.com) — always the
  // landing page, regardless of session. Its CTAs hand off cross-subdomain to
  // the master app host (chitos.<apex>/signup + /login), where the real
  // signup/login flow runs. Tenant + master hosts fall through below.
  if (is_marketing_host()) {
    return (
      <Routes>
        <Route path="/docs" element={<DocsPage />} />
        <Route path="/docs/:slug" element={<DocsPage />} />
        <Route path="/terms" element={<LegalPage doc="terms" />} />
        <Route path="/privacy" element={<LegalPage doc="privacy" />} />
        <Route path="/refund" element={<LegalPage doc="refund" />} />
        <Route path="/contact" element={<LegalPage doc="contact" />} />
        <Route path="*" element={<LandingPage />} />
      </Routes>
    );
  }

  const on_master = is_master_host();

  if (!session) {
    if (on_master) {
      return (
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/docs/:slug" element={<DocsPage />} />
          <Route path="/terms" element={<LegalPage doc="terms" />} />
          <Route path="/privacy" element={<LegalPage doc="privacy" />} />
          <Route path="/refund" element={<LegalPage doc="refund" />} />
          <Route path="/contact" element={<LegalPage doc="contact" />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/set-password" element={<SetPasswordPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      );
    }
    // Tenant subdomain — bare URL goes to /login. No landing page.
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/set-password" element={<SetPasswordPage />} />
        <Route path="/sub/login" element={<SubLogin />} />
        <Route path="/sub/*" element={<Navigate to="/sub/login" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  if (session.kind === 'subscriber') {
    return (
      <SubShell>
        <Routes>
          <Route path="/sub" element={<SubHome />} />
          <Route path="/sub/chits" element={<SubHome />} />
          <Route path="/sub/history" element={<SubHistory />} />
          <Route path="/sub/credit" element={<SubCredit />} />
          <Route path="/sub/chit/:chit_id" element={<SubChitDetail />} />
          <Route path="/sub/pay/:subscription_id" element={<SubPay />} />
          <Route path="*" element={<Navigate to="/sub" replace />} />
        </Routes>
      </SubShell>
    );
  }

  // Staff (operator) session — same Shell on master + tenant hosts. The
  // admin/* routes are mounted unconditionally because the underlying
  // procs enforce platform:write; the sidebar nav items are only added
  // when the session has the scope (see Shell.tsx).
  return (
    <Shell>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/dues" element={<DuesPage />} />
        <Route path="/chits" element={<ChitsPage />} />
        <Route path="/chits/new" element={<ChitsNewPage />} />
        <Route path="/chits/:chit_id" element={<ChitDetailPage />} />
        <Route path="/subscribers" element={<SubscribersPage />} />
        <Route path="/subscribers/new" element={<SubscribersNewPage />} />
        <Route path="/subscribers/:subscriber_id" element={<SubscriberDetailPage />} />
        <Route path="/subscribers/:subscriber_id/chits/:chit_id" element={<SubscriberChitDetailPage />} />
        <Route path="/whatsapp" element={<WhatsAppPage />} />
        <Route path="/calls" element={<FeatureGate feature="voice" title_en="Voice reminder calls" title_ta="குரல் நினைவூட்டல் அழைப்புகள்"><CallsPage /></FeatureGate>} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/reports/collection-day" element={<ReportsCollectionDayPage />} />
        <Route path="/reports/aging" element={<ReportsAgingPage />} />
        <Route path="/reports/day-book" element={<ReportsDayBookPage />} />
        <Route path="/reports/foreman-commission" element={<ReportsForemanCommissionPage />} />
        <Route path="/reports/member-statement" element={<ReportsMemberStatementPage />} />
        <Route path="/reports/chit-pnl" element={<ReportsChitPnlPage />} />
        <Route path="/reports/payout-forecast" element={<ReportsPayoutForecastPage />} />
        <Route path="/reports/comms" element={<ReportsCommsPage />} />
        <Route path="/transactions" element={<TransactionsPage />} />
        {/* Backward-compat: old /cashbook URL → /transactions. Existing
            bookmarks + saved sidebar shortcuts keep working. */}
        <Route path="/cashbook" element={<Navigate to="/transactions" replace />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/channels" element={<SettingsChannelsPage />} />
        <Route path="/verify" element={<VerifyPage />} />
        <Route path="/tax" element={<FeatureGate feature="tax_export" title_en="Tax filing & Tally export" title_ta="வரி தாக்கல் & Tally ஏற்றுமதி"><TaxPage /></FeatureGate>} />
        {/* Billing moved into Settings; keep /billing as a redirect for old links. */}
        <Route path="/billing" element={<Navigate to="/settings?tab=billing" replace />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/admin/tenants" element={<AdminTenants />} />
        <Route path="/admin/tenants/:tenant_id" element={<AdminTenantDetail />} />
        <Route path="/admin/plans" element={<AdminPlans />} />
        <Route path="/admin/transactions" element={<AdminTransactions />} />
        <Route path="/admin/flags" element={<AdminFlags />} />
        <Route path="/admin/analytics" element={<AdminAnalytics />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Shell>
  );
}
